/**
 * CONFIGURACIÓN DEL SEGUIMIENTO AUTOMÁTICO — valores por defecto, normalización y validación,
 * en un solo sitio para la UI y el backend.
 *
 * ⚠️ COPIA BYTE-IDÉNTICA en `src/utils/seguimientoAutoConfig.js`. Cualquier cambio va en las
 * dos, y `functions/scripts/check-shared-module-parity.js` bloquea el commit si divergen.
 * La copia de `src/` es `.js` porque CRA no resuelve `.cjs`; el contenido es el mismo.
 *
 * POR QUÉ COMPARTIDO Y NO DOS IMPLEMENTACIONES: la UI escribe `bots/{botId}/integrations/
 * config.seguimientoAuto` y el barrido lo lee. Si la UI aceptara 4 toques y el backend
 * recortara a 3, o la UI guardara horas como cadenas y el backend esperara números, el
 * operador vería una cosa y el bot haría otra, sin error en ninguno de los dos lados. Aquí
 * `documentoDesdeFormulario` es lo que la UI PERSISTE y `normalizar` lo que el backend LEE,
 * y un test comprueba que lo uno pasa por lo otro sin un solo ajuste.
 *
 * DOS TOPES DE HORAS, y es a propósito (docs/SEGUIMIENTO.md §2.2, bloque 2):
 *   · `MAX_HORAS_UI = 22`: lo que el formulario deja escribir. El bloque 1 mostró que un
 *     toque a 23 h exactas nunca sale a su hora —cae siempre en la regla nocturna y puede
 *     invertir la escalera—, así que la UI no lo ofrece.
 *   · `MAX_HORAS_MOTOR = 23`: lo que `planDeToques` sigue aceptando, como defensa para un
 *     documento escrito por otro camino. `normalizar` recorta a este valor, no al de la UI:
 *     un dato que el motor puede procesar no se destruye por venir de fuera del formulario.
 *
 * `primerToqueMirada` (bloque 6, C3b, decisión de Rodrigo del 29-sep-2026): con `true`, el
 * primer toque es exactamente «👀» —sin modelo, sin botones— en vez de la reformulación; el
 * último toque sigue siendo la salida fácil con botones, así que con un solo toque no aplica.
 * Default `false`. Ausente NO es un ajuste: los documentos anteriores a este campo no lo
 * tienen y el lector no debe avisar por ellos. Lo decide `peldanoDe` en `redactarToque.js`.
 * La UI todavía no lo pinta; `formularioDesdeDocumento` lo trae y `documentoDesdeFormulario`
 * lo devuelve, así que una UI que guarde lo conserva (test de ida y vuelta en la paridad).
 *
 * `seguimientoASaludos` (bloque 8, decisión de Rodrigo del 1-oct-2026): ¿se abre seguimiento
 * cuando el cliente SÓLO saludó? Default `true` y AL REVÉS que `primerToqueMirada`: ausente es
 * `true`, y sólo un `false` explícito lo apaga. Los documentos anteriores al campo (Sofía) siguen
 * abriendo fichas para los saludos, que es lo que hacían. Un saludo abre como mucho dos toques
 * («👀» y la salida fácil, sin modelo); lo decide `ficha.prepararFicha`.
 *
 * `resolverZonaInicial` recibe la función país→zona inyectada porque las dos copias viven en
 * árboles distintos y `timezoneMap` está en rutas distintas en cada uno (C5: la cadena de
 * `generateAlerts.js:1795` sólo para el valor inicial; después manda lo guardado).
 *
 * `listaBlanca` (bloque 7, docs/SEGUIMIENTO.md §2.6, requisito de Rodrigo del 30-sep-2026):
 * array opcional de `chatId`. Con lista NO VACÍA el barrido sólo manda toques a esos chats y
 * cierra el resto con `fuera-de-lista-blanca`; AUSENTE o VACÍA significa «todos». Es un
 * andamio de validación en producción, no una función de producto:
 *   · el formulario de la UI NO la expone ni la escribe (`documentoDesdeFormulario` no la
 *     devuelve): se escribe a mano en Firestore. Sobrevive a un guardado desde la UI porque
 *     `BotIntegrationsPanel` hace `setDoc(..., { merge: true })`, que no borra un campo que el
 *     objeto no trae (medido el 29-sep-2026 con `primerToqueMirada`);
 *   · `normalizar` sólo la incluye en la config cuando el documento la trae, así un documento
 *     anterior al campo no cambia de forma ni produce ajuste. Con la clave presente pero sin
 *     forma de array de cadenas, se anota `lista-blanca-invalida` y se descarta lo que no sea
 *     cadena (una lista con basura no puede convertirse en «todos» por accidente: si queda
 *     vacía tras filtrar, sí rige para todos, y el ajuste lo dice).
 *
 * `ordenToques` (decisión de Rodrigo del 19-oct-2026): el orden de los peldaños. AUSENTE es el de
 * siempre —1) reformular o «👀» según `primerToqueMirada`, 2) dato útil, 3) salida fácil—, y con
 * `'dato-primero'` es 1) dato útil, 2) «👀», 3) salida fácil. Lo aplica `peldanoDe`. Igual que
 * `listaBlanca`: la UI no lo expone ni lo escribe, se pone a mano en Firestore, sobrevive al
 * `merge: true` del panel, y `normalizar` sólo lo incluye si el documento lo trae (un documento
 * sin el campo no cambia de forma). Un valor que no está en `ORDENES_TOQUES` se descarta con el
 * ajuste `orden-toques-invalido`, y rige el orden de siempre.
 */

const MAX_TOQUES = 3;
const MAX_HORAS_UI = 22;
const MAX_HORAS_MOTOR = 23;
const ZONA_DEFECTO = 'America/Santiago';
const HORARIO_DEFECTO = Object.freeze({ desde: '09:00', hasta: '21:00' });

/** Lo que se crea al abrir la config por primera vez. `timeZone` la pone `resolverZonaInicial`. */
const CONFIG_DEFECTO = Object.freeze({
  activado: false,
  toques: 3,
  horas: Object.freeze([1, 6, 22]),
  horarioPermitido: HORARIO_DEFECTO,
  primerToqueMirada: false,
  seguimientoASaludos: true,
});

/** Los valores admitidos de `ordenToques` (ver la cabecera). Ausente = el orden de siempre. */
const ORDENES_TOQUES = Object.freeze(['dato-primero']);

const RE_HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

function zonaValida(tz) {
  if (typeof tz !== 'string' || !tz) return false;
  try { new Intl.DateTimeFormat('en-US', { timeZone: tz }); return true; } catch (_e) { return false; }
}

function minutos(hhmm) {
  const [h, m] = String(hhmm).split(':').map(Number);
  return h * 60 + m;
}

function horarioValido(h) {
  return !!h && RE_HHMM.test(h.desde) && RE_HHMM.test(h.hasta) && minutos(h.desde) < minutos(h.hasta);
}

/**
 * La zona con la que nace la config: `timeZone` del tenant, si no la del país, si no Chile.
 * Es la cadena de `generateAlerts.js:1795`, y sólo se usa para el valor inicial.
 */
function resolverZonaInicial(tenant, getTimezoneFromCountry) {
  const t = tenant || {};
  if (zonaValida(t.timeZone)) return t.timeZone;
  const porPais = typeof getTimezoneFromCountry === 'function' ? getTimezoneFromCountry(t.country) : null;
  if (zonaValida(porPais)) return porPais;
  return ZONA_DEFECTO;
}

/**
 * Lo que el backend LEE: cualquier cosa guardada → una config completa y coherente, más la
 * lista de lo que hubo que ajustar (para el log; vacía si el documento venía bien).
 *
 * @param {object|null|undefined} raw   el campo `seguimientoAuto` tal como está en Firestore
 * @param {{ zonaPorDefecto?: string }} [opciones]
 * @returns {{ config: object, ajustes: string[] }}
 */
function normalizar(raw, opciones = {}) {
  const zonaPorDefecto = zonaValida(opciones.zonaPorDefecto) ? opciones.zonaPorDefecto : ZONA_DEFECTO;
  // Sin documento no hay nada que ajustar: es el estado normal de un bot que nunca abrió la
  // pantalla, y el lector no debe avisar por él. Un solo ajuste, `sin-config`, y los defaults.
  if (raw == null) {
    return {
      config: { ...CONFIG_DEFECTO, horas: [...CONFIG_DEFECTO.horas], horarioPermitido: { ...HORARIO_DEFECTO }, timeZone: zonaPorDefecto },
      ajustes: ['sin-config'],
    };
  }
  const ajustes = [];
  const r = typeof raw === 'object' ? raw : {};
  if (typeof raw !== 'object') ajustes.push('config-no-es-objeto');

  const activado = r.activado === true;

  let horas = Array.isArray(r.horas) ? r.horas.map(Number).filter((h) => Number.isFinite(h) && h > 0) : [];
  if (!Array.isArray(r.horas) || horas.length !== r.horas.length) ajustes.push('horas-invalidas');
  const recortadas = horas.map((h) => Math.min(h, MAX_HORAS_MOTOR));
  if (recortadas.some((h, i) => h !== horas[i])) ajustes.push('horas-recortadas');
  horas = [...new Set(recortadas)].sort((a, b) => a - b);
  if (horas.length !== recortadas.length || horas.some((h, i) => h !== recortadas[i])) ajustes.push('horas-ordenadas');
  if (horas.length > MAX_TOQUES) { horas = horas.slice(0, MAX_TOQUES); ajustes.push('toques-recortados'); }
  if (horas.length === 0) { horas = [...CONFIG_DEFECTO.horas]; ajustes.push('horas-por-defecto'); }

  const toques = horas.length;
  if (Number(r.toques) !== toques) ajustes.push('toques-derivados-de-horas');

  let horarioPermitido = r.horarioPermitido;
  if (!horarioValido(horarioPermitido)) { horarioPermitido = { ...HORARIO_DEFECTO }; ajustes.push('horario-por-defecto'); }
  else horarioPermitido = { desde: horarioPermitido.desde, hasta: horarioPermitido.hasta };

  let timeZone = r.timeZone;
  if (!zonaValida(timeZone)) { timeZone = zonaPorDefecto; ajustes.push('zona-por-defecto'); }

  // Booleano estricto, como `activado`: ausente o cualquier otra cosa → false, sin ajuste.
  const primerToqueMirada = r.primerToqueMirada === true;
  // Al revés: sólo un `false` explícito lo apaga (ver la cabecera).
  const seguimientoASaludos = r.seguimientoASaludos !== false;

  const config = { activado, toques, horas, horarioPermitido, timeZone, primerToqueMirada, seguimientoASaludos };

  // Lista blanca: sólo entra en la config si el documento la trae (ver la cabecera).
  if (r.listaBlanca !== undefined && r.listaBlanca !== null) {
    const cruda = Array.isArray(r.listaBlanca) ? r.listaBlanca : [];
    const listaBlanca = cruda.filter((c) => typeof c === 'string' && c.trim() !== '');
    if (!Array.isArray(r.listaBlanca) || listaBlanca.length !== cruda.length) ajustes.push('lista-blanca-invalida');
    config.listaBlanca = listaBlanca;
  }

  // Orden de los toques: como la lista blanca, sólo entra si el documento lo trae.
  if (r.ordenToques !== undefined && r.ordenToques !== null) {
    if (ORDENES_TOQUES.includes(r.ordenToques)) config.ordenToques = r.ordenToques;
    else ajustes.push('orden-toques-invalido');
  }

  return { config, ajustes };
}

/**
 * Lo que la UI comprueba ANTES de guardar. Devuelve códigos, no textos: la UI los traduce.
 * Aquí el tope es el de la UI (22), no el del motor.
 */
function validarFormulario(form) {
  const errores = [];
  const f = form || {};
  const horas = Array.isArray(f.horas) ? f.horas.map(Number) : [];
  if (!Number.isInteger(f.toques) || f.toques < 1 || f.toques > MAX_TOQUES) errores.push('toques-rango');
  if (horas.length === 0 || horas.length !== f.toques) errores.push('horas-cantidad');
  if (horas.some((h) => !Number.isFinite(h) || h <= 0 || h > MAX_HORAS_UI)) errores.push('hora-rango');
  if (horas.some((h, i) => i > 0 && h <= horas[i - 1])) errores.push('horas-no-ascendentes');
  if (!horarioValido(f.horarioPermitido)) errores.push('horario-invalido');
  if (!zonaValida(f.timeZone)) errores.push('zona-invalida');
  return errores;
}

/**
 * Lo que la UI PERSISTE, exactamente. Un solo escritor con una sola forma.
 * NO devuelve `listaBlanca` a propósito: la UI no la gestiona, y el `merge: true` del panel
 * conserva la que haya en Firestore.
 */
function documentoDesdeFormulario(form) {
  return {
    activado: form.activado === true,
    toques: Number(form.toques),
    horas: form.horas.map(Number),
    horarioPermitido: { desde: form.horarioPermitido.desde, hasta: form.horarioPermitido.hasta },
    timeZone: form.timeZone,
    primerToqueMirada: form.primerToqueMirada === true,
    seguimientoASaludos: form.seguimientoASaludos !== false,
  };
}

/** Lo que la UI MUESTRA al abrir: la config guardada normalizada, o la de defecto con la zona resuelta. */
function formularioDesdeDocumento(raw, zonaInicial) {
  if (raw == null) return { ...CONFIG_DEFECTO, horas: [...CONFIG_DEFECTO.horas], horarioPermitido: { ...HORARIO_DEFECTO }, timeZone: zonaInicial };
  return normalizar(raw, { zonaPorDefecto: zonaInicial }).config;
}

module.exports = {
  MAX_TOQUES, MAX_HORAS_UI, MAX_HORAS_MOTOR, ZONA_DEFECTO, HORARIO_DEFECTO, CONFIG_DEFECTO, ORDENES_TOQUES,
  zonaValida, resolverZonaInicial, normalizar, validarFormulario, documentoDesdeFormulario, formularioDesdeDocumento,
};
